import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
    Alert,
    AppBar,
    Box,
    Button,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Toolbar,
    Typography,
} from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import ReplayIcon from '@mui/icons-material/Replay';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import config from '../../config';

const MINIO_BUCKET = (config as any).MINIO_BUCKET || 'fermi-docs';
const TOKEN_KEY = 'mobile-scan-token';
const MAX_DIMENSION = 1920;
const JPEG_QUALITY = 0.82;

type Phase = 'initializing' | 'ready' | 'captured' | 'uploading' | 'error';

/** Compress image to fit within MAX_DIMENSION and JPEG_QUALITY */
const compressImage = (file: File): Promise<File> => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
            let { width, height } = img;

            // Scale down if larger than MAX_DIMENSION
            if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
                if (width > height) {
                    height = Math.round(height * (MAX_DIMENSION / width));
                    width = MAX_DIMENSION;
                } else {
                    width = Math.round(width * (MAX_DIMENSION / height));
                    height = MAX_DIMENSION;
                }
            }

            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            if (!ctx) { resolve(file); return; }
            ctx.drawImage(img, 0, 0, width, height);

            canvas.toBlob(
                (blob) => {
                    if (!blob) { resolve(file); return; }
                    const compressed = new File(
                        [blob],
                        file.name.replace(/\.\w+$/, '.jpg'),
                        { type: 'image/jpeg' }
                    );
                    resolve(compressed);
                },
                'image/jpeg',
                JPEG_QUALITY
            );
        };
        img.onerror = () => reject(new Error('Falha ao processar imagem'));
        img.src = URL.createObjectURL(file);
    });
};

const getBaseUrl = (): string => {
    const base: string = (config as any).BASE_URL;
    if (base.includes('localhost')) {
        return base.replace('localhost', window.location.hostname);
    }
    return base;
};

const createAxios = (token: string) => {
    const instance = axios.create({ baseURL: getBaseUrl() });
    instance.interceptors.request.use((cfg) => {
        if (cfg.headers) {
            cfg.headers.Authorization = token;
        }
        return cfg;
    });
    return instance;
};

const MobileScanPage = () => {
    const { personId: personIdParam } = useParams<{ personId: string }>();
    const personId = Number(personIdParam);

    const [phase, setPhase] = useState<Phase>('initializing');
    const [errorMsg, setErrorMsg] = useState('');
    const [personName, setPersonName] = useState('');
    const [documentTypes, setDocumentTypes] = useState<iDocumentType[]>([]);
    const [selectedTypeId, setSelectedTypeId] = useState<number>(0);
    const [sentCount, setSentCount] = useState(0);

    const [capturedImage, setCapturedImage] = useState<string | null>(null);
    const [capturedFile, setCapturedFile] = useState<File | null>(null);

    const fileInputRef = useRef<HTMLInputElement>(null);
    const axiosRef = useRef<ReturnType<typeof createAxios> | null>(null);

    // --- Init: read token from URL, validate, fetch person name ---
    useEffect(() => {
        const init = async () => {
            const urlParams = new URLSearchParams(window.location.search);
            let token = urlParams.get('token') || '';
            if (token) {
                localStorage.setItem(TOKEN_KEY, token);
                window.history.replaceState({}, '', window.location.pathname);
            } else {
                token = localStorage.getItem(TOKEN_KEY) || '';
            }

            if (!token) {
                setErrorMsg('Token de acesso não encontrado. Gere um novo QR code no desktop.');
                setPhase('error');
                return;
            }

            const api = createAxios(token);
            axiosRef.current = api;

            try {
                const personRes = await api.get(`/Persons/${personId}`);
                setPersonName(personRes.data?.Name || `Aluno #${personId}`);
            } catch (e: any) {
                if (e?.response?.status === 401) {
                    setErrorMsg('Sessão expirada. Gere um novo QR code no desktop.');
                } else {
                    setErrorMsg(`Erro ao validar acesso. Verifique o link e tente novamente.`);
                }
                localStorage.removeItem(TOKEN_KEY);
                setPhase('error');
                return;
            }

            try {
                const filter = JSON.stringify({ where: { active: true }, order: 'order ASC' });
                const typesRes = await api.get(`/DocumentTypes?filter=${filter}`);
                setDocumentTypes(typesRes.data || []);
            } catch {
                // Non-blocking
            }

            setPhase('ready');
        };

        init();

        return () => {
            localStorage.removeItem(TOKEN_KEY);
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Cleanup on page close
    useEffect(() => {
        const cleanup = () => localStorage.removeItem(TOKEN_KEY);
        window.addEventListener('beforeunload', cleanup);
        return () => window.removeEventListener('beforeunload', cleanup);
    }, []);

    // --- Handlers ---
    const handleOpenCamera = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
        const rawFile = e.target.files?.[0];
        if (!rawFile) return;

        // Reset input so same file can be re-selected
        e.target.value = '';

        setSelectedTypeId(0);
        setErrorMsg('');

        // Compress image before preview/upload
        try {
            const file = await compressImage(rawFile);
            setCapturedFile(file);

            const reader = new FileReader();
            reader.onload = (ev) => {
                setCapturedImage(ev.target?.result as string);
                setPhase('captured');
            };
            reader.readAsDataURL(file);
        } catch {
            setErrorMsg('Falha ao processar a imagem. Tente novamente.');
        }
    }, []);

    const handleRecapture = () => {
        setCapturedImage(null);
        setCapturedFile(null);
        setErrorMsg('');
        fileInputRef.current?.click();
    };

    const handleBackToReady = () => {
        setCapturedImage(null);
        setCapturedFile(null);
        setErrorMsg('');
        setPhase('ready');
    };

    const handleUpload = async () => {
        if (!capturedFile || !axiosRef.current) return;

        setPhase('uploading');
        try {
            // 1. Upload file to MinIO
            const formData = new FormData();
            formData.append('file', capturedFile);
            const uploadRes = await axiosRef.current.post(
                `/PersonDocumentContainers/${MINIO_BUCKET}/upload`,
                formData
            );
            const fileData = uploadRes.data.result.files.file[0];

            // 2. Create metadata record
            const docData: iPersonDocument = {
                personId,
                documentTypeId: selectedTypeId || undefined,
                fileName: fileData.name,
                originalName: capturedFile.name,
                fileSize: capturedFile.size,
                mimeType: capturedFile.type,
                uploadDate: new Date(),
            };
            await axiosRef.current.post('/PersonDocuments', docData);

            setSentCount((prev) => prev + 1);
            setCapturedImage(null);
            setCapturedFile(null);
            setSelectedTypeId(0);
            setPhase('ready');
        } catch (e: any) {
            if (e?.response?.status === 401) {
                setErrorMsg('Sessão expirada. Gere um novo QR code no desktop.');
                localStorage.removeItem(TOKEN_KEY);
                setPhase('error');
            } else {
                const detail = e?.response?.data?.error?.message || e?.response?.data?.message || e?.message || '';
                setErrorMsg(`Erro ao enviar: ${e?.response?.status || 'sem conexão'} - ${detail}`);
                setPhase('captured');
            }
        }
    };

    // --- Render ---
    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', display: 'flex', flexDirection: 'column' }}>
            {/* Hidden file input — opens native camera on mobile */}
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleFileChange}
                style={{ display: 'none' }}
            />

            {/* Top bar */}
            <AppBar position="static" color="primary" elevation={1}>
                <Toolbar variant="dense">
                    <Typography variant="h6" sx={{ flexGrow: 1, fontSize: '1rem' }}>
                        Digitalização de Documentos
                    </Typography>
                </Toolbar>
            </AppBar>

            {/* Initializing */}
            {phase === 'initializing' && (
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, p: 3 }}>
                    <CircularProgress size={48} />
                    <Typography color="text.secondary">Validando acesso...</Typography>
                </Box>
            )}

            {/* Error */}
            {phase === 'error' && (
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, p: 3 }}>
                    <Alert severity="error" sx={{ width: '100%', maxWidth: 400 }}>
                        {errorMsg}
                    </Alert>
                </Box>
            )}

            {/* Ready */}
            {phase === 'ready' && (
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3, p: 3 }}>
                    <Typography variant="h6" textAlign="center">
                        {personName}
                    </Typography>

                    {sentCount > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CheckCircleOutlineIcon color="success" />
                            <Typography color="success.main">
                                {sentCount} documento{sentCount > 1 ? 's' : ''} enviado{sentCount > 1 ? 's' : ''}
                            </Typography>
                        </Box>
                    )}

                    <Button
                        variant="contained"
                        size="large"
                        startIcon={<PhotoCameraIcon />}
                        onClick={handleOpenCamera}
                        sx={{ py: 2, px: 4, fontSize: '1.1rem' }}
                    >
                        Fotografar Documento
                    </Button>
                </Box>
            )}

            {/* Captured / Uploading */}
            {(phase === 'captured' || phase === 'uploading') && capturedImage && (
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    {/* Preview */}
                    <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: '#e0e0e0', p: 2, overflow: 'auto' }}>
                        <img
                            src={capturedImage}
                            alt="Documento fotografado"
                            style={{
                                maxWidth: '100%',
                                maxHeight: '55vh',
                                objectFit: 'contain',
                                borderRadius: 4,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                            }}
                        />
                    </Box>

                    {/* Upload error */}
                    {phase === 'captured' && errorMsg && (
                        <Alert severity="error" sx={{ mx: 2, mt: 1 }} onClose={() => setErrorMsg('')}>
                            {errorMsg}
                        </Alert>
                    )}

                    {/* Type selection + actions */}
                    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2, bgcolor: '#fff' }}>
                        {documentTypes.length > 0 && (
                            <FormControl fullWidth size="small">
                                <InputLabel>Tipo de Documento</InputLabel>
                                <Select
                                    value={selectedTypeId}
                                    label="Tipo de Documento"
                                    onChange={(e) => setSelectedTypeId(Number(e.target.value))}
                                    disabled={phase === 'uploading'}
                                >
                                    <MenuItem value={0}><em>Selecionar tipo...</em></MenuItem>
                                    {documentTypes.map((dt) => (
                                        <MenuItem key={dt.id} value={dt.id}>
                                            {dt.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}

                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                            <Button
                                onClick={handleBackToReady}
                                disabled={phase === 'uploading'}
                                size="small"
                            >
                                Cancelar
                            </Button>
                            <Button
                                startIcon={<ReplayIcon />}
                                onClick={handleRecapture}
                                disabled={phase === 'uploading'}
                            >
                                Refazer Foto
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={phase === 'uploading' ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                                onClick={handleUpload}
                                disabled={phase === 'uploading' || !capturedFile}
                            >
                                {phase === 'uploading' ? 'Enviando...' : 'Enviar'}
                            </Button>
                        </Box>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default MobileScanPage;
